import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input, Modal, Spin, Tag, message } from 'antd';
import { CheckCircleFilled, LeftOutlined } from '@ant-design/icons';
import api from '../api';

const ACTION_W = 148; // 查看详情 + 增加备注

export default function InventoryRoom() {
  const { taskId, locationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_count: 0, checked_count: 0, unchecked_count: 0 });
  const [locationName, setLocationName] = useState('');
  const [taskStatus, setTaskStatus] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [openSwipeId, setOpenSwipeId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [remarkAsset, setRemarkAsset] = useState(null);
  const [remarkText, setRemarkText] = useState('');
  const [remarkSaving, setRemarkSaving] = useState(false);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const movedRef = useRef(false);
  const readonly = Number(taskStatus) === 2;

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/inventory/tasks/${taskId}/locations/${locationId}/assets`, {
      params: { only: filter === 'all' ? 'all' : filter },
    })
      .then((res) => {
        setItems(res?.items || []);
        setStats({
          total_count: res?.total_count || 0,
          checked_count: res?.checked_count || 0,
          unchecked_count: res?.unchecked_count || 0,
        });
        if (res?.task_status != null) setTaskStatus(res.task_status);
        const name = res?.items?.[0]?.location_name;
        if (name) setLocationName(name);
      })
      .catch((err) => message.error(err.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [taskId, locationId, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (locationName) return undefined;
    let cancelled = false;
    api.get(`/inventory/tasks/${taskId}/locations`)
      .then((res) => {
        if (cancelled) return;
        const loc = (res?.items || []).find((x) => String(x.id) === String(locationId));
        if (loc?.location_name) setLocationName(loc.location_name);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [taskId, locationId, locationName]);

  const toggleCheck = async (asset) => {
    if (readonly) {
      message.info('任务已完成，不可再改标记');
      return;
    }
    if (busyId) return;
    setBusyId(asset.id);
    const nextChecked = !asset.checked;
    try {
      await api.put(`/inventory/tasks/${taskId}/assets/${asset.id}/check`, {
        checked: nextChecked,
      });
      setItems((prev) => {
        const next = prev.map((a) => (
          a.id === asset.id ? { ...a, checked: nextChecked } : a
        ));
        if (filter === 'checked' && !nextChecked) return next.filter((a) => a.id !== asset.id);
        if (filter === 'unchecked' && nextChecked) return next.filter((a) => a.id !== asset.id);
        return next;
      });
      setStats((prev) => {
        const delta = nextChecked ? 1 : -1;
        const checked_count = Math.max(0, Number(prev.checked_count || 0) + delta);
        return {
          ...prev,
          checked_count,
          unchecked_count: Math.max(0, Number(prev.total_count || 0) - checked_count),
        };
      });
    } catch (err) {
      message.error(err.message || '标记失败');
    } finally {
      setBusyId(null);
    }
  };

  const handleTouchStart = (e, id) => {
    touchStartXRef.current = e.touches?.[0]?.clientX || 0;
    touchStartYRef.current = e.touches?.[0]?.clientY || 0;
    movedRef.current = false;
    setDraggingId(id);
    setDragOffset(openSwipeId === id ? -ACTION_W : 0);
  };

  const handleTouchMove = (e, id) => {
    if (draggingId !== id) return;
    const x = e.touches?.[0]?.clientX || 0;
    const y = e.touches?.[0]?.clientY || 0;
    const dx = x - touchStartXRef.current;
    const dy = y - touchStartYRef.current;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) movedRef.current = true;
    if (Math.abs(dy) > Math.abs(dx)) return;
    const base = openSwipeId === id ? -ACTION_W : 0;
    setDragOffset(Math.max(-ACTION_W, Math.min(0, base + dx)));
  };

  const handleTouchEnd = (id) => {
    if (draggingId !== id) return;
    const shouldOpen = dragOffset < -ACTION_W / 2;
    setOpenSwipeId(shouldOpen ? id : null);
    setDragOffset(0);
    setDraggingId(null);
  };

  const openRemark = (asset) => {
    setOpenSwipeId(null);
    setRemarkAsset(asset);
    setRemarkText(asset.remark || asset.note || '');
  };

  const saveRemark = async () => {
    if (!remarkAsset) return;
    const content = (remarkText || '').trim();
    if (!content) {
      message.warning('请填写备注内容');
      return;
    }
    if (readonly) {
      message.info('任务已完成，不可再改备注');
      return;
    }
    setRemarkSaving(true);
    try {
      const res = await api.put(
        `/inventory/tasks/${taskId}/assets/${remarkAsset.id}/remark`,
        { content },
      );
      setItems((prev) => prev.map((a) => (
        a.id === remarkAsset.id
          ? {
            ...a,
            remark: res?.content || content,
            note: res?.content || content,
            remark_id: res?.id || a.remark_id,
            remark_confirmed: false,
          }
          : a
      )));
      message.success('备注已保存');
      setRemarkAsset(null);
    } catch (err) {
      message.error(err.message || '保存失败');
    } finally {
      setRemarkSaving(false);
    }
  };

  const filters = [
    { key: 'all', label: `全部 ${stats.total_count}` },
    { key: 'unchecked', label: `未盘 ${stats.unchecked_count}` },
    { key: 'checked', label: `已盘 ${stats.checked_count}` },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', paddingBottom: 70 }}>
      <div style={{
        display: 'flex', alignItems: 'center', padding: '12px 16px',
        background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <LeftOutlined onClick={() => navigate(`/inventory/${taskId}`)} style={{ fontSize: 18, marginRight: 12 }} />
        <span style={{ fontSize: 17, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {locationName || '房间盘点'}
        </span>
        {readonly ? <Tag color="green">已完成</Tag> : null}
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '10px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        {filters.map((f) => (
          <div
            key={f.key}
            onClick={() => { setFilter(f.key); setOpenSwipeId(null); }}
            style={{
              padding: '4px 12px', borderRadius: 14, fontSize: 13, cursor: 'pointer',
              background: filter === f.key ? '#1677ff' : '#f5f7fa',
              color: filter === f.key ? '#fff' : '#666',
            }}
          >
            {f.label}
          </div>
        ))}
      </div>

      <Spin spinning={loading}>
        <div style={{ padding: 16 }}>
          {items.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 50 }}>暂无资产</div>
          ) : (
            items.map((a) => {
              const offset = draggingId === a.id
                ? dragOffset
                : (openSwipeId === a.id ? -ACTION_W : 0);
              return (
                <div
                  key={a.id}
                  style={{ position: 'relative', marginBottom: 8, borderRadius: 10, overflow: 'hidden' }}
                >
                  <div style={{
                    position: 'absolute', right: 0, top: 0, bottom: 0, width: ACTION_W,
                    display: 'flex',
                  }}
                  >
                    <div
                      onClick={() => {
                        setOpenSwipeId(null);
                        navigate(`/assets/${a.id}`);
                      }}
                      style={{
                        flex: 1, background: '#1677ff', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 500,
                      }}
                    >
                      查看详情
                    </div>
                    <div
                      onClick={() => openRemark(a)}
                      style={{
                        flex: 1, background: '#fa8c16', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 500,
                      }}
                    >
                      增加备注
                    </div>
                  </div>

                  <div
                    onTouchStart={(e) => handleTouchStart(e, a.id)}
                    onTouchMove={(e) => handleTouchMove(e, a.id)}
                    onTouchEnd={() => handleTouchEnd(a.id)}
                    onClick={() => {
                      if (movedRef.current || openSwipeId === a.id) {
                        if (openSwipeId === a.id) setOpenSwipeId(null);
                        return;
                      }
                      toggleCheck(a);
                    }}
                    style={{
                      position: 'relative',
                      zIndex: 1,
                      background: '#fff',
                      borderRadius: 10,
                      padding: '12px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      border: a.checked ? '1px solid #b7eb8f' : '1px solid transparent',
                      opacity: busyId === a.id ? 0.6 : 1,
                      cursor: readonly ? 'default' : 'pointer',
                      transform: `translateX(${offset}px)`,
                      transition: draggingId === a.id ? 'none' : 'transform 0.2s ease',
                      touchAction: 'pan-y',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{a.asset_name}</div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{a.asset_code || '-'}</div>
                      <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>使用人：{a.user_name || '-'}</div>
                      {(a.remark || a.note) ? (
                        <div style={{
                          marginTop: 6, fontSize: 12, color: '#d46b08',
                          background: '#fff7e6', borderRadius: 6, padding: '4px 8px',
                        }}
                        >
                          备注：{a.remark || a.note}
                        </div>
                      ) : null}
                    </div>
                    {a.checked ? (
                      <CheckCircleFilled style={{ fontSize: 22, color: '#52c41a' }} />
                    ) : (
                      <Tag>未盘</Tag>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Spin>

      <Modal
        title="增加备注"
        open={!!remarkAsset}
        onCancel={() => setRemarkAsset(null)}
        onOk={saveRemark}
        confirmLoading={remarkSaving}
        okText="保存"
        destroyOnClose
        okButtonProps={{ disabled: readonly }}
      >
        {remarkAsset ? (
          <div style={{ marginBottom: 10, fontSize: 13, color: '#666' }}>
            {remarkAsset.asset_name}
            {remarkAsset.asset_code ? `（${remarkAsset.asset_code}）` : ''}
          </div>
        ) : null}
        <Input.TextArea
          rows={4}
          maxLength={500}
          showCount
          value={remarkText}
          onChange={(e) => setRemarkText(e.target.value)}
          placeholder="请填写该资产存在的问题或补充说明"
          disabled={readonly}
        />
        <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
          提交盘点完成时，备注会按「房间 + 资产」自动汇总进反馈，供超管逐条确认。
        </div>
      </Modal>
    </div>
  );
}
